import React from 'react';
import { Box, Typography } from '@mui/material';
import { LAP } from '../clock/clockModel';

// The Desk's cycle ruler (DWLF-300; canvas board AppDesk): where this market is between its last
// confirmed weekly low and its stored window — earliest · opens → closes · hard max. Geometry over
// the stored edges; no forecast.
//
// Scale (DWLF-429): at least the Cycle Clock's 1.5-gap lap, stretched to the furthest stored edge
// when the window runs past it, so a wide window is never clipped and the hard max is always drawn.

const tok = (t) => `var(--dwlf-${t})`;
const LABEL = { fontFamily: '"Space Mono", monospace', textTransform: 'uppercase', letterSpacing: '0.1em' };

/** The ruler's full length, in typical gaps (p units): the lap, or the furthest stored edge past it. */
const rulerScale = (arcs) => Math.max(LAP, ...['closes', 'hardMax'].map((k) => arcs?.[k]).filter(Number.isFinite));

/** Percent position of p on a ruler of length `scale`, clamped to the bar. */
const rulerPct = (p, scale) => (Math.max(0, Math.min(p, scale)) / scale) * 100;

export default function CycleRuler({ cycle }) {
  if (!cycle?.onDial) return null;
  const { arcs, p } = cycle;
  const scale = rulerScale(arcs);
  const pct = (v) => `${rulerPct(v, scale)}%`;
  const seg = (from, to, token) => (from == null || to == null || to <= from ? null : (
    <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: pct(from), width: `calc(${pct(to)} - ${pct(from)})`, backgroundColor: tok(token) }} />
  ));
  // "Window" sits centred under opens → closes; "Hard max" stays right-aligned at the bar end, where the
  // hard max lands whenever it sets the scale (and clear of the window label when it does not).
  const label = ({ text, token, at, shift }) => (
    <Typography component="span" sx={{ ...LABEL, position: 'absolute', top: 0, whiteSpace: 'nowrap', fontSize: '0.5625rem', color: tok(token), left: at, transform: `translateX(${shift})` }}>{text}</Typography>
  );
  const windowMid = arcs.opens != null && arcs.closes != null ? (arcs.opens + arcs.closes) / 2 : null;
  return (
    <Box sx={{ mb: 1.5 }} role="img"
      aria-label={`Week ${Math.round(cycle.weeksSincePivot)} since the last confirmed low; window ${arcs.opens == null ? 'unknown' : 'opens'}`}>
      <Box sx={{ position: 'relative', height: 14, backgroundColor: tok('ink-06') }}>
        {seg(arcs.earliest, arcs.opens, 'warn-15')}
        {seg(arcs.opens, arcs.closes, 'warn-40')}
        {seg(arcs.closes, arcs.hardMax, 'down-20')}
        <Box sx={{ position: 'absolute', top: -4, bottom: -4, width: 3, left: `calc(${pct(p)} - 1px)`, backgroundColor: tok('text') }} />
      </Box>
      <Box sx={{ position: 'relative', height: '1.1rem', mt: 0.5 }}>
        {label({ text: 'Last low', token: 'accent', at: '0%', shift: '0' })}
        {windowMid != null && label({ text: 'Window', token: 'warn', at: pct(windowMid), shift: '-50%' })}
        {arcs.hardMax != null && label({ text: 'Hard max', token: 'down', at: '100%', shift: '-100%' })}
      </Box>
    </Box>
  );
}
